/* TipsSheet — Fullscreen guide "Come usare ORIO al meglio".
   Mostra tutti i tips/suggerimenti con immagine ORIO + icona + body.
   Aperta dall'ultima FeatureSlide della Home.
*/
import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  X, Sparkles, AlertTriangle, Stethoscope, Heart, Briefcase, Footprints,
  QrCode, Sun, HeartHandshake, Camera, ShieldCheck, ArrowRight, BookOpen,
} from "lucide-react";

import { orioIllustration, orioIllustrationFallback } from "@/lib/illustrations";
import { hapticTap } from "@/lib/haptics";

// Curated guide content — each tip has its own image (already in /backend/static/illustrations/)
const GUIDE = [
  {
    section: "Primo passo",
    items: [
      {
        slug: "feat-create-dog", icon: Sparkles, color: "#C25612",
        title: "Crea il passaporto digitale",
        body: "Foto, microchip, libretto e razza. Bastano 60 secondi per avere il tuo cane sempre in tasca.",
        to: "/app/cani/nuovo", cta: "Aggiungi cane",
      },
      {
        slug: "tip-microchip", icon: ShieldCheck, color: "#FF7A1A",
        title: "Il microchip è il dato più importante",
        body: "Chi trova il tuo cane può leggere il microchip dal QR pubblico — niente chiamate al canile.",
        to: "/app/cani", cta: "Aggiungi microchip",
      },
      {
        slug: "tip-qr", icon: QrCode, color: "#5B9EE0",
        title: "QR pubblico sul collare",
        body: "Stampa il QR e mettilo sul collare. Chi lo inquadra ti chiama in un tocco — niente dati sensibili visibili.",
        to: "/app/cani", cta: "Scarica QR",
      },
    ],
  },
  {
    section: "A spasso",
    items: [
      {
        slug: "tip-walking", icon: Footprints, color: "#FF7A1A",
        title: "Walking mode protetto",
        body: "Quando passeggi, ORIO ti avvisa di pericoli e di altri walker vicini. Walk insieme = strada più sicura.",
        to: "/app/match", cta: "Avvia passeggiata",
      },
      {
        slug: "tip-meteo", icon: Sun, color: "#FF7A1A",
        title: "Meteo dog-friendly",
        body: "L'app legge la temperatura dell'asfalto: se brucia le zampe, te lo dice prima di uscire.",
        to: "/app", cta: "Vedi meteo",
      },
      {
        slug: "feat-match", icon: Heart, color: "#1E7E3F",
        title: "Match per camminare, non chattare",
        body: "Niente DM infinite: dopo il match si propone direttamente un orario per una vera passeggiata.",
        to: "/app/match", cta: "Inizia a fare swipe",
      },
    ],
  },
  {
    section: "Sicurezza & community",
    items: [
      {
        slug: "tip-hazard", icon: Camera, color: "#FFB020",
        title: "Segnala un pericolo in 1 tap",
        body: "Vetri, bocconi, cane sciolto, sostanze tossiche: foto + posizione = altri padroni avvisati entro 1 km. +3 karma.",
        to: "/app/mappa", cta: "Apri mappa", highlight: true,
      },
      {
        slug: "tip-sos", icon: AlertTriangle, color: "#E94545",
        title: "SOS a onde concentriche",
        body: "Se hai perso il cane, l'allerta parte a 10 km e cresce fino a 80 km in 10 minuti, raggiungendo solo chi è davvero vicino.",
        to: "/app/mappa", cta: "Scopri SOS",
      },
      {
        slug: "tip-vet", icon: Stethoscope, color: "#2B7CE9",
        title: "Veterinario in 2 tap",
        body: "Dall'icona vet sulla mappa chiami direttamente il più vicino in apertura, anche di domenica e notte.",
        to: "/app/mappa?filter=vet", cta: "Cerca vet",
      },
    ],
  },
  {
    section: "Organizzati",
    items: [
      {
        slug: "tip-cassetto", icon: Briefcase, color: "#8B6A14",
        title: "Cassetto digitale cifrato",
        body: "Libretto, vaccini e ricette sempre con te — anche offline. Promemoria automatici quando una scadenza si avvicina.",
        to: "/app/cassetto", cta: "Apri cassetto",
      },
      {
        slug: "tip-karma", icon: Sparkles, color: "#F4B83C",
        title: "Karma per chi aiuta",
        body: "Ogni segnalazione, missione e match valida accumula karma. Puoi spendere karma per Super Like Match o altri vantaggi.",
        to: "/app/profilo", cta: "Vedi karma",
      },
      {
        slug: "tip-shelter", icon: HeartHandshake, color: "#2DBA60",
        title: "Rifugi e adozioni in chiaro",
        body: "Rifugi verificati con cani adottabili, storia, carattere ed esigenze. Niente intermediari, contatto diretto.",
        to: "/app/missioni", cta: "Esplora rifugi",
      },
    ],
  },
];

/* ───────────────────────────────────────────────────────────── */
export default function TipsSheet({ open, onClose }) {
  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1600] flex items-end sm:items-center justify-center pb-[160px] sm:pb-0"
        data-testid="tips-sheet"
      >
        <div className="absolute inset-0 bg-black/55 backdrop-blur-md" onClick={onClose} />

        <motion.div
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 280 }}
          className="relative w-full sm:max-w-md mx-0 sm:mx-3 rounded-t-[28px] sm:rounded-[28px] bg-white border border-[#F0EBE3] overflow-hidden flex flex-col"
          style={{ maxHeight: "min(86dvh, calc(100dvh - 170px))", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.40)" }}
        >
          {/* Header */}
          <div className="relative px-5 pt-5 pb-3 border-b border-[#F0EBE3]">
            <div aria-hidden className="absolute -top-10 -right-10 h-32 w-32 rounded-full opacity-50 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(255,122,26,0.30) 0%, transparent 60%)" }} />
            <div aria-hidden className="absolute -top-8 -left-12 h-28 w-28 rounded-full opacity-40 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(123,97,255,0.25) 0%, transparent 60%)" }} />
            <div className="relative flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#FF7A1A] inline-flex items-center gap-1.5">
                  <BookOpen className="h-3 w-3" /> Guida ORIO
                </div>
                <h2 className="mt-1 font-display text-2xl text-[#1A1A1A] leading-tight">
                  Come usare ORIO al meglio
                </h2>
                <p className="mt-0.5 text-[12px] text-[#6B6B6B] max-w-xs">
                  Suggerimenti pratici per te e il tuo cane, con esempi e scorciatoie.
                </p>
              </div>
              <button
                onClick={onClose}
                data-testid="tips-sheet-close"
                aria-label="Chiudi"
                className="h-9 w-9 rounded-full bg-[#F4EFE6] hover:bg-[#EAE3D2] flex items-center justify-center"
              >
                <X className="h-4 w-4 text-[#6B6B6B]" />
              </button>
            </div>
          </div>

          {/* Sections */}
          <div className="overflow-y-auto px-4 py-3 space-y-5">
            {GUIDE.map((sec, si) => (
              <motion.div
                key={sec.section}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: si * 0.05 }}
              >
                <div className="text-[10px] uppercase tracking-[0.20em] text-[#9A9A9A] font-semibold mb-2 px-1">
                  {sec.section}
                </div>
                <div className="space-y-2">
                  {sec.items.map((t) => (
                    <TipCard key={t.title} tip={t} onClose={onClose} />
                  ))}
                </div>
              </motion.div>
            ))}

            <div className="text-center text-[11px] text-[#9A9A9A] py-4">
              ORIO è infrastruttura per il mondo canino · Aggiornamenti continui
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function TipCard({ tip, onClose }) {
  const Icon = tip.icon;
  return (
    <Link
      to={tip.to}
      onClick={() => { hapticTap(); onClose?.(); }}
      data-testid={`tip-card-${tip.slug}`}
      className="block relative rounded-2xl overflow-hidden border border-[#F0EBE3] bg-white hover:bg-[#FBF7F1] active:scale-[0.99] transition-all"
      style={{
        boxShadow: tip.highlight
          ? `0 0 0 2px ${tip.color}33, 0 10px 24px -12px ${tip.color}55`
          : "0 4px 14px -10px rgba(0,0,0,0.10)",
      }}
    >
      <div className="flex items-stretch gap-0">
        {/* Image */}
        <div
          className="relative w-[110px] shrink-0 overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${tip.color}10 0%, ${tip.color}05 100%)`,
          }}
        >
          <img
            src={orioIllustration(tip.slug)}
            onError={(e) => { e.currentTarget.src = orioIllustrationFallback(tip.slug); }}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {tip.highlight && (
            <motion.div aria-hidden
              animate={{ x: ["-50%", "150%"] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              className="absolute top-0 left-0 h-full w-1/3 opacity-[0.45] pointer-events-none"
              style={{ background: "linear-gradient(120deg, transparent, rgba(255,255,255,0.85), transparent)", filter: "blur(6px)" }}
            />
          )}
        </div>

        {/* Content */}
        <div className="relative flex-1 min-w-0 p-3.5">
          <div className="flex items-start gap-2 mb-1">
            <div className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: `${tip.color}1A`, color: tip.color }}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-display text-[15px] leading-tight text-[#1A1A1A]">{tip.title}</h4>
            </div>
          </div>
          <p className="text-[11.5px] text-[#5C5C5C] leading-snug">{tip.body}</p>
          <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold"
            style={{ color: tip.color }}>
            {tip.cta} <ArrowRight className="h-3 w-3" />
          </div>
        </div>
      </div>
    </Link>
  );
}
